/**
 * Persists OEIS “search sequence” input and the last terms-search HTTP payload in this browser’s
 * NullaPad storage (localStorage, `nullapad.*` keys — same family as `nullapad.instanceSecret.v1`).
 */

const LAST_TERMS_QUERY_KEY = "nullapad.oeisLastTermsQuery.v1";
const LAST_SEARCH_SNAPSHOT_KEY = "nullapad.oeisLastSearchSnapshot.v1";
const MAX_SNAPSHOT_BODY_CHARS = 512 * 1024;

/** Last text in the “Search sequence” toolbar field (may be partial while typing). */
export function persistNullapadOeisLastTermsQuery(query: string): void {
  try {
    localStorage.setItem(LAST_TERMS_QUERY_KEY, query);
  } catch {
    /* quota / private mode */
  }
}

export function loadNullapadOeisLastTermsQuery(): string {
  try {
    return localStorage.getItem(LAST_TERMS_QUERY_KEY) ?? "";
  } catch {
    return "";
  }
}

export type NullapadOeisSearchSnapshotV1 = {
  v: 1;
  /** Normalized comma-separated terms sent as OEIS `q`. */
  query: string;
  requestUrl: string;
  httpStatus: number;
  fetchedAtMs: number;
  /** Raw response body (JSON or error text), possibly truncated. */
  body: string;
};

function isSnapshotV1(o: unknown): o is NullapadOeisSearchSnapshotV1 {
  if (!o || typeof o !== "object") return false;
  const r = o as Record<string, unknown>;
  return (
    r.v === 1 &&
    typeof r.query === "string" &&
    typeof r.requestUrl === "string" &&
    typeof r.httpStatus === "number" &&
    typeof r.fetchedAtMs === "number" &&
    typeof r.body === "string"
  );
}

/** Store the full last OEIS terms search (URL + status + raw body) for this NullaPad instance. */
export function persistNullapadOeisSearchSnapshot(s: Omit<NullapadOeisSearchSnapshotV1, "v">): void {
  let body = s.body;
  if (body.length > MAX_SNAPSHOT_BODY_CHARS) {
    body = body.slice(0, MAX_SNAPSHOT_BODY_CHARS) + "\n…[truncated]\n";
  }
  const payload: NullapadOeisSearchSnapshotV1 = {
    v: 1,
    query: s.query,
    requestUrl: s.requestUrl,
    httpStatus: s.httpStatus,
    fetchedAtMs: s.fetchedAtMs,
    body,
  };
  try {
    localStorage.setItem(LAST_SEARCH_SNAPSHOT_KEY, JSON.stringify(payload));
  } catch {
    try {
      localStorage.setItem(
        LAST_SEARCH_SNAPSHOT_KEY,
        JSON.stringify({
          ...payload,
          body: "[omitted: response too large for storage]",
        }),
      );
    } catch {
      /* ignore */
    }
  }
}

export function loadNullapadOeisSearchSnapshot(): NullapadOeisSearchSnapshotV1 | null {
  try {
    const raw = localStorage.getItem(LAST_SEARCH_SNAPSHOT_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as unknown;
    return isSnapshotV1(o) ? o : null;
  } catch {
    return null;
  }
}
